import { Entity } from '@backstage/catalog-model';

/**
 * The TestOps project a service's tests report to: its numeric id. Not
 * `qameta.io/allure-project` — that one names an allure-docker-service project
 * by string, and the community plugin reads it.
 */
export const TESTOPS_PROJECT_ANNOTATION = 'qameta.io/testops-project-id';

export function testopsProject(entity: Entity): string | undefined {
  const value =
    entity.metadata.annotations?.[TESTOPS_PROJECT_ANNOTATION]?.trim();
  return value && /^\d+$/.test(value) ? value : undefined;
}

/** As the backend (modules/allureTestops) sends them, in the card's order. */
export const STATUSES = [
  'passed',
  'failed',
  'broken',
  'skipped',
  'unknown',
] as const;

export type Status = (typeof STATUSES)[number];
export type Statistic = Record<Status, number>;

export type Launch = {
  id: number;
  name: string;
  createdDate: number;
  closed: boolean;
  statistic: Statistic;
};

/**
 * TestOps' own pages, from the URL scheme of its UI. Not yet checked against
 * our instance — if a link lands wrong, this is the one place to fix.
 */
const root = (baseUrl: string) => baseUrl.replace(/\/+$/, '');
export const projectUrl = (baseUrl: string, projectId: string) =>
  `${root(baseUrl)}/project/${projectId}/launches`;
export const launchUrl = (baseUrl: string, launchId: number) =>
  `${root(baseUrl)}/launch/${launchId}`;

export const total = (statistic: Statistic) =>
  STATUSES.reduce((sum, status) => sum + statistic[status], 0);

/** What went wrong in a launch: failed tests plus broken ones. */
export const failures = (statistic: Statistic) =>
  statistic.failed + statistic.broken;

/** Passed over every result, skipped included — what the bar shows. */
export const passRate = (statistic: Statistic) =>
  statistic.passed / Math.max(total(statistic), 1);

/**
 * What the card draws out of a project's newest launches:
 * - `latest` — the newest closed launch with results; its numbers are final,
 *   so it is the headline. An open launch is still filling and its rate would
 *   jump between refreshes.
 * - `running` — an open launch newer than that, shown as in progress.
 * - `trend` — the closed launches with results, oldest to newest.
 */
export function summarise(launches: Launch[]) {
  const newest = [...launches].sort((a, b) => b.createdDate - a.createdDate);
  const closed = newest.filter(l => l.closed && total(l.statistic) > 0);
  const latest = closed.at(0);
  const running = newest.find(
    l => !l.closed && (!latest || l.createdDate > latest.createdDate),
  );
  return { latest, running, trend: closed.reverse() };
}
